import styled, { keyframes } from 'styled-components';
import { useEffect } from 'react';
import { IconCheck } from '@shared/ui/icons';
import type { EvidenceUsed } from '@entities/legal/types';

interface Props {
  open: boolean;
  /** Заголовок: «Пункт X.Y, категория». */
  title: string;
  severity: string;
  /** Дословная цитата фрагмента. null/пусто = показать заглушку. */
  clauseQuote?: string | null;
  /** Что говорит агент про этот фрагмент. */
  description: string;
  /** Встречная редакция (proposed_change или proposed_action). */
  proposed?: string | null;
  /** «Почему агент так решил». null/пусто = блок не рисуем. */
  evidence?: EvidenceUsed | null;
  /** Файл комплекта, в котором найден риск. null/пусто = блок не рисуем. */
  sourceDocument?: string | null;
  onClose: () => void;
}

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0.2; }
  to   { transform: translateX(0);     opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: ${({ theme }) => theme.z.modal};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(560px, 92vw);
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  display: flex;
  flex-direction: column;
  z-index: ${({ theme }) => theme.z.modal + 1};
  animation: ${slideIn} ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(5)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const Close = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(5)};
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: ${({ theme }) => theme.spacing(4)} 0 ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  gap: 6px;
  &:first-child {
    margin-top: 0;
  }
`;

const Quote = styled.blockquote`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Para = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ProposedBox = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.status.success}11;
  border: 1px solid ${({ theme }) => theme.colors.status.success}33;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
`;

const EvidenceBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  line-height: 1.55;
`;

const EvidenceGroup = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(3)};
    padding-top: ${({ theme }) => theme.spacing(3)};
    border-top: 1px dashed ${({ theme }) => theme.colors.border};
  }
`;

const EvidenceLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const EvidenceItem = styled.div`
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.text.primary};
  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

/** Путь к файлу внутри комплекта — длинный, ломать его переносом нельзя. */
const DocPath = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const SevPill = styled.span<{ $sev: string }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger + '22'
      : $sev === 'major'
        ? theme.colors.status.warning + '22'
        : theme.colors.surface};
  color: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger
      : $sev === 'major'
        ? theme.colors.status.warning
        : theme.colors.text.muted};
`;

export function RiskClausePanel({
  open,
  title,
  severity,
  clauseQuote,
  description,
  proposed,
  evidence,
  sourceDocument,
  onClose,
}: Props) {
  const precedents = evidence?.precedent_quotes ?? [];
  const laws = evidence?.law_articles ?? [];
  const templates = evidence?.template_clauses ?? [];
  const hasEvidence = precedents.length + laws.length + templates.length > 0;
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel role="dialog" aria-label={title}>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <SevPill $sev={severity}>
              {severity === 'critical'
                ? 'критическое'
                : severity === 'major'
                  ? 'важное'
                  : 'minor'}
            </SevPill>
            <Title>{title}</Title>
          </div>
          <Close type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </Close>
        </Header>
        <Body>
          {sourceDocument && sourceDocument.trim() && (
            <>
              <SectionLabel>Файл комплекта</SectionLabel>
              <DocPath>{sourceDocument.trim()}</DocPath>
            </>
          )}

          <SectionLabel>
            <IconCheck size={12} />
            Исходный текст пункта
          </SectionLabel>
          {clauseQuote && clauseQuote.trim() ? (
            <Quote>«{clauseQuote.trim()}»</Quote>
          ) : (
            <Empty>
              LLM не выделил конкретный фрагмент — обычно для рисков
              «отсутствует раздел» или для риска, добавленного юристом вручную.
              Смотрите описание ниже и оригинал документа.
            </Empty>
          )}

          <SectionLabel>Что говорит агент</SectionLabel>
          <Para>{description}</Para>

          {proposed && proposed.trim() && (
            <>
              <SectionLabel>Встречная редакция / предложение</SectionLabel>
              <ProposedBox>{proposed.trim()}</ProposedBox>
            </>
          )}

          {hasEvidence && (
            <>
              <SectionLabel>Обоснование агента</SectionLabel>
              <EvidenceBox>
                {precedents.length > 0 && (
                  <EvidenceGroup>
                    <EvidenceLabel>Похожие случаи из практики юристов</EvidenceLabel>
                    {precedents.map((q, i) => (
                      <EvidenceItem key={`p-${i}`}>«{q}»</EvidenceItem>
                    ))}
                  </EvidenceGroup>
                )}
                {laws.length > 0 && (
                  <EvidenceGroup>
                    <EvidenceLabel>Применённые нормы права</EvidenceLabel>
                    {laws.map((l, i) => (
                      <EvidenceItem key={`l-${i}`}>{l}</EvidenceItem>
                    ))}
                  </EvidenceGroup>
                )}
                {templates.length > 0 && (
                  <EvidenceGroup>
                    <EvidenceLabel>Отклонения от типовой формы АО «САТКОМ»</EvidenceLabel>
                    {templates.map((t, i) => (
                      <EvidenceItem key={`t-${i}`}>{t}</EvidenceItem>
                    ))}
                  </EvidenceGroup>
                )}
              </EvidenceBox>
            </>
          )}
        </Body>
      </Panel>
    </>
  );
}
